import React from 'react'
import Dialog from '@material-ui/core/Dialog'
import Typography from '@material-ui/core/Typography'
import {
  Link,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@material-ui/core'
import { FormattedMessage } from 'react-intl'
import messages from '../../messages'
import { makeStyles } from '@material-ui/core/styles'
import DialogContent from '@material-ui/core/DialogContent'
import MuiDialogTitle from '@material-ui/core/DialogTitle'
import CloseIcon from '@material-ui/icons/Close'
import { TubeProduct } from '../../utils/enum'
import CheckMarkIcon from '@src/icons/CheckMarkIcon'

const useStyles = makeStyles((theme) => ({
  typography: {
    padding: theme.spacing(0),
  },
  tableBody: {
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(1),
  },
  rootRow: {
    fontWeight: theme.typography.fontWeightMedium,
    padding: theme.spacing(2),
    backgroundColor: '#F2F3F4',
    width: '20%',
  },

  table: {
    minWidth: 800,
    padding: theme.spacing(1),
    left: theme.spacing(0),
  },
  root: {
    margin: 0,
    marginTop: 0,
  },
  clearButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
  styleLink: {
    color: theme.palette.primary.main,
  },
  contentArea: {
    padding: theme.spacing(2),
  },
  redesignContainer: {
    '& p': {
      padding: theme.spacing(8),
    },
    '& $root': {
      textTransform: 'uppercase',
      position: 'relative',
      padding: theme.spacing(0, 0, 6),
    },
    '& $clearButton': {
      padding: theme.spacing(1),
      right: 0,
      top: 0,
      '& svg': {
        width: theme.typography.pxToRem(24),
        height: theme.typography.pxToRem(24),
        color: theme.palette.text.primary,
      },
    },
    '& $contentArea': {
      padding: 0,
    },
    '& $rootRow': {
      backgroundColor: '#F8F8FC',
    },
    '& $tableBody': {
      padding: theme.spacing(2, 1),
    },
    '& $table': {
      '& td:first-child': {
        paddingLeft: theme.spacing(3),
      },
      '& th:first-child': {
        paddingLeft: theme.spacing(3),
      },
    },
  },
  checkIcon: {
    width: theme.spacing(3.5),
    height: theme.spacing(3),
  },
}))
export interface DialogTitleProps {
  id: string
  children: React.ReactNode
  onClose: () => void
}

const DialogTitle = (props: DialogTitleProps) => {
  const { children, onClose, ...other } = props
  const classes = useStyles()
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.clearButton}
          onClick={onClose}
          color="primary"
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  )
}

interface RecommendationPopUpProps {
  isValid: boolean
  productType: string
  isRedesign?: boolean
}

const RecommendationPopUp: React.FC<RecommendationPopUpProps> = (props) => {
  const classes = useStyles()
  const [anchorEl, setAnchorEl] = React.useState(null)
  const isValid = props.isValid
  const { productType, isRedesign } = props

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'modified-popover' : undefined

  function createData(application, desalt, cartridge, HPLC, page) {
    return { application, desalt, cartridge, HPLC, page }
  }
  const checkedSymbol = isRedesign ? (
    <CheckMarkIcon color="primary" className={classes.checkIcon} />
  ) : (
    'X'
  )
  const rows = [
    createData('Standard PCR & RT-PCR', checkedSymbol, checkedSymbol, '', ''),
    createData('Sequencing', checkedSymbol, checkedSymbol, '', ''),
    createData('QPCR Probes', '', '', checkedSymbol, ''),
    createData('Cloning', '', '', checkedSymbol, checkedSymbol),
    createData(
      'Microarrays',
      checkedSymbol,
      checkedSymbol,
      checkedSymbol,
      checkedSymbol
    ),
    createData('siRNA Screening', checkedSymbol, checkedSymbol, '', ''),
    createData('Antisense', '', '', checkedSymbol, checkedSymbol),
    createData('Bioengineering', '', '', checkedSymbol, checkedSymbol),
    createData('Crystallography,NMR', '', '', checkedSymbol, checkedSymbol),
    createData('In-vivo application', '', '', checkedSymbol, ''),
  ]
  const rowsForRna = [
    createData('siRNA Screening', checkedSymbol, checkedSymbol, '', ''),
    createData('Antisense', '', '', checkedSymbol, checkedSymbol),
    createData('Bioengineering', '', '', checkedSymbol, checkedSymbol),
    createData('Crystallography,NMR', '', '', checkedSymbol, checkedSymbol),
    createData('In-vivo application', '', '', checkedSymbol, ''),
  ]
  return (
    <div>
      <Link
        component="button"
        underline={isValid ? 'none' : 'hover'}
        disabled={isValid}
        onClick={handleClick}
        className={classes.styleLink}
      >
        {isRedesign ? (
          <FormattedMessage {...messages.CONF_RECOMMENDATION_standard} />
        ) : (
          <i>
            <FormattedMessage {...messages.CONF_RECOMMENDATION_standard} />
          </i>
        )}
      </Link>

      <Dialog
        id={id}
        open={open}
        onClose={handleClose}
        maxWidth="md"
        className={isRedesign ? classes.redesignContainer : undefined}
      >
        <Typography className={classes.typography}>
          <DialogTitle id="customized-dialog-title" onClose={handleClose}>
            <b>
              <FormattedMessage
                {...messages.CONF_RECOMMENDATION_POPUP_standard}
              />
            </b>
          </DialogTitle>
          <DialogContent dividers={!isRedesign} className={classes.contentArea}>
            <Table className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell align="left" className={classes.rootRow}>
                    <FormattedMessage {...messages.CONF_POPUP_APPLICATION} />
                  </TableCell>
                  <TableCell align="center" className={classes.rootRow}>
                    <FormattedMessage {...messages.CONF_DESALT} />
                  </TableCell>
                  <TableCell align="center" className={classes.rootRow}>
                    <FormattedMessage {...messages.CONF_CARTRIDGE} />
                  </TableCell>
                  <TableCell align="center" className={classes.rootRow}>
                    <FormattedMessage {...messages.CONF_POPUP_HPLC} />
                  </TableCell>
                  <TableCell align="center" className={classes.rootRow}>
                    <FormattedMessage {...messages.CONF_POPUP_PAGE} />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <>
                  {productType === TubeProduct.Rna
                    ? rowsForRna.map((row, index) => (
                        <TableRow className={classes.rootRow} key={index}>
                          <TableCell align="left" className={classes.tableBody}>
                            {row.application}
                          </TableCell>
                          <TableCell
                            align="center"
                            className={classes.tableBody}
                          >
                            {row.desalt}
                          </TableCell>
                          <TableCell
                            align="center"
                            className={classes.tableBody}
                          >
                            {row.cartridge}
                          </TableCell>
                          <TableCell
                            align="center"
                            className={classes.tableBody}
                          >
                            {row.HPLC}
                          </TableCell>
                          <TableCell
                            align="center"
                            className={classes.tableBody}
                          >
                            {row.page}
                          </TableCell>
                        </TableRow>
                      ))
                    : rows.map((row, index) => (
                        <TableRow className={classes.rootRow} key={index}>
                          <TableCell align="left" className={classes.tableBody}>
                            {row.application}
                          </TableCell>
                          <TableCell
                            align="center"
                            className={classes.tableBody}
                          >
                            {row.desalt}
                          </TableCell>
                          <TableCell
                            align="center"
                            className={classes.tableBody}
                          >
                            {row.cartridge}
                          </TableCell>
                          <TableCell
                            align="center"
                            className={classes.tableBody}
                          >
                            {row.HPLC}
                          </TableCell>
                          <TableCell
                            align="center"
                            className={classes.tableBody}
                          >
                            {row.page}
                          </TableCell>
                        </TableRow>
                      ))}
                </>
              </TableBody>
            </Table>
          </DialogContent>
        </Typography>
      </Dialog>
    </div>
  )
}
export default RecommendationPopUp
